import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Clock, CheckCircle2, Users, Calendar, Trophy, BookOpen, Heart, MessageCircle, Play, TrendingUp, Zap, Timer, Youtube, User, LayoutGrid, HelpCircle, Layers, Bell, Check, Infinity } from "lucide-react";
import { Link } from "react-router-dom";

const IndexFr = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="CSW - Salle d'Étude Virtuelle | Espace de Travail en Ligne | Outil de Productivité | Study Stream"
        description="Salles d'étude virtuelles gratuites et espace de travail en ligne pour étudiants. Rejoignez des sessions d'étude 24h/24, utilisez des outils de productivité, minuteur Pomodoro, et connectez-vous avec des apprenants du monde entier."
        keywords="salle d'étude virtuelle, espace de travail en ligne, étude en ligne, study stream, minuteur pomodoro, étudier ensemble"
        canonicalUrl="/fr/"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
              {/* Left Content */}
              <div className="space-y-8 animate-fade-in">
                <div className="space-y-4">
                  <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-foreground leading-tight">
                    Salle d'Étude Virtuelle
                  </h1>
                  <p className="text-2xl sm:text-3xl text-muted-foreground font-light">
                    Un espace pour améliorer votre concentration et vos habitudes d'étude
                  </p>
                </div>

                <p className="text-lg text-muted-foreground leading-relaxed">
                  La salle d'étude en ligne, notamment CSW (Chill Study Work), est un outil qui a transformé ma façon d'étudier. J'avais du mal à me concentrer à la maison et à gérer mon temps, mais grâce aux fonctionnalités et règles de la salle d'étude en ligne, mon efficacité d'apprentissage s'est nettement améliorée.
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    asChild
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 transition-all hover:scale-105"
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Rejoindre Maintenant
                    </a>
                  </Button>
                  <Button 
                    asChild
                    variant="outline"
                    size="lg"
                    className="font-semibold px-8 group"
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                      <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
                      Essayer
                    </a>
                  </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-8 pt-4">
                  <div>
                    <div className="text-4xl font-bold text-foreground">100k+</div>
                    <div className="text-muted-foreground mt-1">Heures d'étude accomplies</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-foreground">8k+</div>
                    <div className="text-muted-foreground mt-1">Tâches complétées</div>
                  </div>
                </div>
              </div>

              {/* Right Video */}
              <div className="relative animate-fade-in" style={{ animationDelay: "0.2s" }}>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-border/50 bg-card/50 backdrop-blur-sm">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 opacity-50" />
                  <video 
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    className="w-full h-auto relative z-10"
                  >
                    <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                  </video>
                </div>
                {/* Floating Stats Cards */}
                <div className="absolute -bottom-6 -left-6 bg-card/90 backdrop-blur-sm border-2 border-border/50 rounded-xl p-4 shadow-lg animate-fade-in" style={{ animationDelay: "0.4s" }}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-primary-foreground" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Productivité améliorée</div>
                      <div className="text-xl font-bold text-foreground">+40%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Modes Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 space-y-4">
              <div className="inline-block px-4 py-2 bg-primary/10 rounded-full mb-4">
                <span className="text-sm font-semibold text-primary uppercase tracking-wider">Rencontrez de nouvelles personnes</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
                Sans pression de communication
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Un environnement où vous pouvez vous connecter naturellement sans stress.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Chill Mode */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Heart className="w-8 h-8 text-blue-500" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Chill</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Étudiez dans une ambiance détendue et relaxante.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    Avec musique de fond
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    Chat libre
                  </li>
                </ul>
              </Card>

              {/* Study Mode */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 border-primary hover:border-primary transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <BookOpen className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Study</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Vivez un environnement d'apprentissage concentré.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    Minuteur Pomodoro
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    Suivi des études
                  </li>
                </ul>
              </Card>

              {/* Work Mode */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-purple-500/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-600/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Zap className="w-8 h-8 text-purple-500" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Work</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Un espace pour maximiser votre productivité.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-purple-500" />
                    Gestion des tâches
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-purple-500" />
                    Suivi des résultats
                  </li>
                </ul>
              </Card>
            </div>

            <div className="text-center mt-12">
              <Button 
                asChild
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8"
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Commencer !
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 space-y-4">
              <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
                Fonctionnalités de la Salle d'Étude Virtuelle
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                CSW offre un environnement confortable pour améliorer l'efficacité de l'apprentissage. Nous proposons des fonctionnalités et outils pratiques pour enregistrer et revoir vos progrès.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Feature 1 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <BookOpen className="w-8 h-8 text-primary-foreground" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Suivi et Révision des Études</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Enregistrez vos heures de début et de fin d'étude et visualisez votre temps d'apprentissage. Cela vous permet de suivre facilement votre rythme et votre niveau de concentration.
                </p>
              </Card>

              {/* Feature 2 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <MessageCircle className="w-8 h-8 text-primary-foreground" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Espace d'Étude Libre et Chat</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Profitez d'un espace d'étude ouvert à tous et interagissez avec d'autres utilisateurs via les réactions et le chat. Ces échanges contribuent grandement à maintenir votre motivation.
                </p>
              </Card>

              {/* Feature 3 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Trophy className="w-8 h-8 text-primary-foreground" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Système de Motivation</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Recevez des récompenses selon votre temps d'étude et vos tâches accomplies, rendant l'apprentissage amusant. Divers programmes sont également disponibles pour maintenir votre concentration.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Alone but Never Lonely Section */}
        <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/5 to-background" />
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto items-center">
              <Card className="p-12 bg-card/50 backdrop-blur-sm border-2 hover:border-primary/50 transition-all">
                <h2 className="text-4xl font-bold text-foreground mb-4">Seul, mais jamais isolé</h2>
                <p className="text-xl text-muted-foreground">
                  Un environnement particulièrement adapté aux personnes introverties, où vous pouvez étudier tranquillement tout en vous connectant naturellement avec les autres.
                </p>
              </Card>

              <Card className="p-12 bg-card/50 backdrop-blur-sm border-2 hover:border-primary/50 transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <Heart className="w-12 h-12 text-primary" />
                  <h2 className="text-4xl font-bold text-foreground">Moment de calme</h2>
                </div>
                <p className="text-xl text-muted-foreground">
                  Nous offrons un environnement silencieux pour les moments où vous avez besoin de concentration.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Impact Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 space-y-4">
              <div className="inline-block px-4 py-2 bg-primary/10 rounded-full mb-4">
                <span className="text-sm font-semibold text-primary uppercase tracking-wider">Apprentissage efficace</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
                Amélioration des habitudes et de la motivation
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                En utilisant CSW, j'ai particulièrement ressenti un impact significatif sur la formation de mes habitudes d'étude.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Impact 1 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all">
                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                  <Calendar className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">Formation d'habitudes de vie</h3>
                <p className="text-muted-foreground">
                  En commençant à étudier à heure fixe chaque jour, ma vie quotidienne est devenue plus régulière.
                </p>
              </Card>

              {/* Impact 2 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all">
                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                  <Users className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">Concentration par observation mutuelle</h3>
                <p className="text-muted-foreground">
                  Savoir que d'autres étudient en même temps crée un sentiment d'accompagnement et aide à maintenir la concentration.
                </p>
              </Card>

              {/* Impact 3 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all">
                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                  <Trophy className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">Sentiment d'accomplissement</h3>
                <p className="text-muted-foreground">
                  Vérifier les enregistrements d'étude renforce le sentiment d'accomplissement et motive à continuer d'apprendre.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Features Grid Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 space-y-4">
              <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
                Outils de Productivité
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Découvrez des fonctionnalités puissantes pour améliorer votre concentration et votre productivité.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {/* Feature Cards */}
              <Card className="p-6 bg-gradient-to-br from-card to-card/50 border hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Timer className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Minuteur Pomodoro</h3>
                <p className="text-sm text-muted-foreground">Sessions de concentration avec pauses automatiques.</p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-card to-card/50 border hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <LayoutGrid className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Tableau Kanban</h3>
                <p className="text-sm text-muted-foreground">Organisez vos tâches visuellement.</p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-card to-card/50 border hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Bell className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Rappels</h3>
                <p className="text-sm text-muted-foreground">Ne manquez jamais une échéance importante.</p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-card to-card/50 border hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <HelpCircle className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Quiz IA</h3>
                <p className="text-sm text-muted-foreground">Testez vos connaissances avec des quiz générés par l'IA.</p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-card to-card/50 border hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Layers className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Flashcards</h3>
                <p className="text-sm text-muted-foreground">Apprenez avec la répétition espacée.</p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-card to-card/50 border hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Youtube className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Study Stream</h3>
                <p className="text-sm text-muted-foreground">Étudiez avec d'autres en temps réel.</p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-card to-card/50 border hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Profil Personnel</h3>
                <p className="text-sm text-muted-foreground">Suivez vos progrès et statistiques.</p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-card to-card/50 border hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Infinity className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Utilisation Illimitée</h3>
                <p className="text-sm text-muted-foreground">Gratuit pour toujours, sans restrictions.</p>
              </Card>
            </div>
          </div>
        </section>

        {/* Study Stream Section */}
        <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16 space-y-4">
              <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
                Study Stream
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Rejoignez des sessions d'étude en direct et restez motivé grâce à la communauté.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="p-8 bg-card/50 backdrop-blur-sm border-2 hover:border-primary/50 transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <Users className="w-10 h-10 text-primary" />
                  <h3 className="text-2xl font-bold text-foreground">Étudiez Ensemble</h3>
                </div>
                <p className="text-muted-foreground">
                  Rejoignez des salles d'étude virtuelles avec des étudiants du monde entier. La présence d'autres personnes augmente naturellement votre motivation et votre concentration.
                </p>
              </Card>

              <Card className="p-8 bg-card/50 backdrop-blur-sm border-2 hover:border-primary/50 transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <Clock className="w-10 h-10 text-primary" />
                  <h3 className="text-2xl font-bold text-foreground">Disponible 24h/24</h3>
                </div>
                <p className="text-muted-foreground">
                  Nos salles d'étude sont ouvertes 24 heures sur 24, 7 jours sur 7. Étudiez quand vous le souhaitez, où que vous soyez.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center space-y-8">
              <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
                Prêt à améliorer votre productivité ?
              </h2>
              <p className="text-xl text-muted-foreground">
                Rejoignez des milliers d'étudiants qui utilisent CSW pour atteindre leurs objectifs d'apprentissage.
              </p>
              <Button 
                asChild
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-12 py-6 text-lg transition-all hover:scale-105"
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Commencer Gratuitement
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default IndexFr;
